import React from 'react';
import { Drawer, Form, Input, Select, Button, Dropdown, Menu, Avatar, message } from 'antd';
import { workwiseServices } from '../../../../api';
import { useSelector } from 'react-redux';
import defaultProfileImage from '../../../../assets/defult-profile-logo.png';
import worldIcon from '../../../../assets/world.svg';
import lockIcon from '../../../../assets/lock.svg';
import { useOfflineToggle } from '../../../OfflineToggle/useOfflineToggle';
import { getUserInfoFromToken } from '../../../../utils/auth';
import {
  createLocalDocument,
  isLocalDocumentLimitReached,
  MAX_LOCAL_DOCUMENTS,
} from '../../../../utils/localDocuments';
import PeopleSelect from './PeopleSelect';
import './CreatePadDrawer.css';

const PRIVACY = {
  PUBLIC: 'public',
  PRIVATE: 'private',
};

export default function CreatePadDrawer({
  open,
  onClose,
  onCreate,
}) {
  const [form] = Form.useForm();
  const [privacy, setPrivacy] = React.useState(PRIVACY.PUBLIC);
  const [collaborators, setCollaborators] = React.useState([]); // array of ids
  const [readers, setReaders] = React.useState([]); // array of ids
  const [isCreating, setIsCreating] = React.useState(false); // loading state
  const modals = useSelector(state => state.editor.modals);
  // Offline drivers document creation to a purely-local flow (no APIs). Uses the
  // shared toggle so the drawer reacts to both browser connectivity and the
  // manual work-offline switch.
  const { offline } = useOfflineToggle();

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      setCollaborators([]);
      setReaders([]);
      setPrivacy(PRIVACY.PUBLIC);
      setIsCreating(false); // reset loading state
    }
  }, [open, form]);

  const menu = (
    <Menu
      onClick={(e) => {
        if (e.key === PRIVACY.PUBLIC) setPrivacy(PRIVACY.PUBLIC);
        if (e.key === PRIVACY.PRIVATE) setPrivacy(PRIVACY.PRIVATE);
      }}
    >
      <Menu.Item key={PRIVACY.PUBLIC}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={worldIcon} alt="Public" style={{ width: 16, height: 16 }} /> Public
        </span>
      </Menu.Item>
      <Menu.Item key={PRIVACY.PRIVATE}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={lockIcon} alt="Private" style={{ width: 16, height: 16 }} /> Private
        </span>
      </Menu.Item>
    </Menu>
  );

  // Offline: create the document entirely in localStorage, no API calls.
  const handleLocalCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return; // validation errors are shown inline by the form
    }

    if (isLocalDocumentLimitReached()) {
      message.warning(
        `You already have ${MAX_LOCAL_DOCUMENTS} local documents. Delete one to create a new local document.`
      );
      return;
    }

    const user = getUserInfoFromToken();
    const creator = user
      ? { id: user.userId || user.id, name: user.name, image: user.image }
      : null;

    const record = createLocalDocument({
      name: values.name.trim(),
      description: values.description || '',
      creator,
    });

    if (!record) {
      message.warning(
        `You already have ${MAX_LOCAL_DOCUMENTS} local documents. Delete one to create a new local document.`
      );
      return;
    }

    message.success('Local document created');
    window.open(`/${record.id}?local=true`, '_blank');

    onCreate?.();
    onClose();
  };

  const handleSubmit = async () => {
    if (offline) {
      return handleLocalCreate();
    }
    try {
      const values = await form.validateFields();
      setIsCreating(true); // start loading
      const formData = new FormData();
      formData.append('name', values.name.trim());
      formData.append('description', values.description || '');
      formData.append('privacyId', privacy === PRIVACY.PUBLIC ? 1 : 2);
      formData.append('referenceId', '00000000-0000-0000-0000-000000000000');
      formData.append('referenceType', 1);
      formData.append('directoryId', '00000000-0000-0000-0000-000000000000');
      formData.append('documentType', 6);
      formData.append('attachments[0].name', values.name.trim());

      let memberIndex = 0;
      if (privacy === PRIVACY.PRIVATE) {
        readers.forEach(id => {
          formData.append(`members[${memberIndex}].memberId`, id);
          formData.append(`members[${memberIndex}].memberRightType`, 1);
          memberIndex++;
        });
      }
      collaborators.forEach(id => {
        formData.append(`members[${memberIndex}].memberId`, id);
        formData.append(`members[${memberIndex}].memberRightType`, 2);
        memberIndex++;
      });
      const response = await workwiseServices.addDocument(formData);
      message.success('Document created successfully');

      // Try to open the newly created document in editor (new tab).
      try {
        const document = response?.data?.data[0];
        const documentId = document?.attachments?.[0]?.attachmentId;
        if (documentId) {
          const templateId = modals?.selectedTemplate?._id;
          const url = templateId ? `/${documentId}/?templateId=${templateId}` : `/${documentId}/`;
          window.open(url, '_blank');
        }
      } catch (openErr) {
        message.error('Document created, but failed to open editor.');
      }

      onCreate?.();
      onClose();

    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to create document';
      message.error(errorMessage);
    } finally {
      setIsCreating(false); // stop loading
    }
  };

  const CreateButtonBar = (
    <div className={`cpd-bar${offline ? ' cpd-bar--solo' : ''}`}>
      {!offline && (
        <Dropdown overlay={menu} placement="topLeft" trigger={["click"]}>
          <div className="cpd-privacy">
            <Button
              type="text"
              shape="circle"
            >
              {privacy === PRIVACY.PUBLIC ? <img src={worldIcon} alt="Public" style={{ width: 16, height: 16 }} /> : <img src={lockIcon} alt="Private" style={{ width: 16, height: 16 }} />}
            </Button>
          </div>
        </Dropdown>
      )}
      <Button
        type="primary"
        size="large"
        block
        className="cpd-create-btn"
        onClick={handleSubmit}
        loading={isCreating}
        disabled={isCreating}
      >
        Create
      </Button>
    </div>
  );

  return (
    <Drawer
      className="cpd-drawer"
      title={<span style={{ fontWeight: 600, color: 'var(--primary-blue-text)' }}>{offline ? 'Create Local Pad' : 'Create Pad'}</span>}
      visible={open}
      onClose={onClose}
      width={640}
      destroyOnClose={false}
      bodyStyle={{
        background: 'var(--surface-sunken)',
        padding: 16,
        height: '100vh'
      }}
    >
      <div className="cpd" style={{
        background: 'var(--surface-raised)',
        borderRadius: 8,
        padding: '20px'
      }}>
        <div>
          <Form form={form} layout="vertical" requiredMark={true}>
            <Form.Item
              label={<span className="cpd-label">Name</span>}
              name="name"
              rules={[{ required: true, message: 'Enter Name' }]}
            >
              <Input placeholder="Enter Name" />
            </Form.Item>

            {!offline && (
              <Form.Item
                label={<span className="cpd-label">Collaborators</span>}
              >
                <PeopleSelect
                  value={collaborators}
                  onChange={setCollaborators}
                  placeholder="Select Collaborators"
                />
              </Form.Item>
            )}

            {!offline && privacy === PRIVACY.PRIVATE && (
              <Form.Item
                label={<span className="cpd-label">Readers</span>}
              >
                <PeopleSelect
                  value={readers}
                  onChange={setReaders}
                  placeholder="Select Member"
                />
              </Form.Item>
            )}

            <Form.Item
              label={<span className="cpd-label">Description</span>}
              name="description"
            >
              <Input.TextArea placeholder="Enter Description" showCount maxLength={500} rows={3} />
            </Form.Item>
          </Form>
        </div>

        <div style={{
          paddingTop: 8
        }}>
          {CreateButtonBar}
        </div>
      </div>
    </Drawer>
  );
}
